"use client";

/**
 * vault #32 — "Vault folders" card on a company's overview: where this company's credentials
 * live (folders the viewer can read), with link / unlink for users who hold SHARE on a folder.
 * Metadata only. Renders nothing for a user who cannot use the vault (client users, a Hub
 * environment without the vault): the RPC refuses them and the card stays out of the way.
 */
import React, { useCallback, useEffect, useState } from "react";
import Link from "next/link";
import { FolderLock, Link2, Unlink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CompanyInfoCard } from "@/features/companies/components/overview/CompanyInfoCard";
import {
  VaultRpcError,
  canUnlink,
  companyVault,
  linkCandidates,
  orderForCard,
  vaultFolderHref,
  type CompanyFoldersResult,
  type LinkableFolder,
} from "./company-folders";

type State =
  | { readonly kind: "loading" }
  | { readonly kind: "hidden" }
  | { readonly kind: "error"; readonly message: string }
  | { readonly kind: "ready"; readonly data: CompanyFoldersResult };

export function CompanyVaultFoldersCard({ companyId }: { companyId: string }) {
  const [state, setState] = useState<State>({ kind: "loading" });
  const [candidates, setCandidates] = useState<LinkableFolder[]>([]);
  const [choice, setChoice] = useState("");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const data = await companyVault.listCompanyFolders(companyId);
      setState({ kind: "ready", data });
      const roots = await companyVault.listRootFolders().catch(() => []);
      setCandidates(linkCandidates(roots, companyId));
    } catch (e) {
      // 401/403/503: not a vault user here, or no vault in this environment — show nothing.
      if (e instanceof VaultRpcError && [401, 403, 503].includes(e.status)) setState({ kind: "hidden" });
      else setState({ kind: "error", message: "Couldn't load vault folders." });
    }
  }, [companyId]);

  useEffect(() => {
    void load();
  }, [load]);

  const change = async (folderId: string, link: boolean) => {
    setBusy(true);
    setNotice(null);
    try {
      const r = await companyVault.setFolderCompany(folderId, link ? companyId : null);
      setNotice(`${link ? "Linked" : "Unlinked"} ${r.updatedFolderIds.length} folder${r.updatedFolderIds.length === 1 ? "" : "s"}.`);
      setChoice("");
      await load();
    } catch (e) {
      setNotice(e instanceof VaultRpcError && e.code === "forbidden" ? "You need Manager access on that folder." : "The vault refused that change.");
    } finally {
      setBusy(false);
    }
  };

  if (state.kind === "hidden") return null;

  return (
    <CompanyInfoCard title="Vault folders" icon={FolderLock}>
      {state.kind === "loading" && <p className="text-sm text-muted-foreground">Loading…</p>}
      {state.kind === "error" && <p className="text-sm text-destructive">{state.message}</p>}
      {state.kind === "ready" && (
        <div className="space-y-3" data-testid="company-vault-folders">
          {state.data.folders.length === 0 ? (
            <p className="text-sm text-muted-foreground">No vault folder you can see is linked to this company.</p>
          ) : (
            <ul className="space-y-1">
              {orderForCard(state.data.folders).map((f) => (
                <li key={f.folderId} className="flex items-center gap-2 text-sm">
                  <Link href={vaultFolderHref(f.folderId)} className={`flex-1 truncate hover:underline ${f.topMost ? "font-medium" : "pl-4 text-muted-foreground"}`}>
                    {f.path.join(" / ")}
                  </Link>
                  <span className="text-xs text-muted-foreground">{f.itemCount} item{f.itemCount === 1 ? "" : "s"}</span>
                  {f.topMost && canUnlink(f) && (
                    <Button size="sm" variant="ghost" disabled={busy} onClick={() => void change(f.folderId, false)} aria-label={`Unlink ${f.name}`} title={`Unlink ${f.name}`}>
                      <Unlink className="h-4 w-4" />
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
          {state.data.hiddenCount !== null && state.data.hiddenCount > 0 && (
            <p className="text-xs text-muted-foreground">
              {state.data.hiddenCount} more linked folder{state.data.hiddenCount === 1 ? "" : "s"} you can&apos;t read.
            </p>
          )}
          {candidates.length > 0 && (
            <div className="flex items-center gap-2">
              <select
                className="h-8 flex-1 rounded-md border border-input bg-background px-2 text-sm"
                value={choice}
                onChange={(e) => setChoice(e.target.value)}
                aria-label="Folder to link"
              >
                <option value="">Link an existing folder…</option>
                {candidates.map((c) => (
                  <option key={c.folderId} value={c.folderId}>
                    {c.name}
                    {c.hubCompanyId ? " (linked elsewhere)" : ""}
                  </option>
                ))}
              </select>
              <Button size="sm" disabled={!choice || busy} onClick={() => void change(choice, true)}>
                <Link2 className="mr-1 h-4 w-4" /> Link
              </Button>
            </div>
          )}
          {notice && <p className="text-xs text-muted-foreground">{notice}</p>}
        </div>
      )}
    </CompanyInfoCard>
  );
}
